import { Box, CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useUnifiedAccountState } from '../../hooks/useUnifiedAccountState';
import { useTranslation } from '../../hooks/useTranslation';

interface IntegrationsRequiredRouteProps {
    children: JSX.Element;
}

const IntegrationsRequiredRoute = ({ children }: IntegrationsRequiredRouteProps) => {
    const { t } = useTranslation();
    const [isCheckingIntegrations, setIsCheckingIntegrations] = useState(true);
    
    // Check if integrations exist
    const accountState = useUnifiedAccountState({ skipIssueFetch: true });
    const hasIntegrations = accountState.hasIntegrations;
    const isLoading = accountState.isProcessing;

    useEffect(() => {
        // Add a small delay to avoid flashing of content while checking integrations
        if (!isLoading) {
            const timeout = setTimeout(() => {
                setIsCheckingIntegrations(false);
            }, 300);
            return () => clearTimeout(timeout);
        }
    }, [isLoading]);

    // Show loading state while checking integrations
    if (isLoading || isCheckingIntegrations) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100vh',
                }}
                role="status"
                aria-label={t('sidebar.status.loadingData')}
            >
                <CircularProgress color="primary" size={40} thickness={4} />
            </Box>
        );
    }

    // No integrations - redirect to integrations page
    if (!hasIntegrations) {
        return <Navigate to="/integrations" replace />;
    }

    // All checks passed, render the protected content
    return children;
};

export default IntegrationsRequiredRoute;